import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-3 box-border whitespace-nowrap text-lg font-bold rounded-lg border focus-visible:outline-4 focus-visible:outline-solid focus-visible:outline-greyscale-400 focus-visible:outline-offset-0 transition-colors disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:fill-current cursor-pointer'
  ),
  {
    variants: {
      variant: {
        primary: 'border-secondary-brand bg-secondary-brand text-contrast-1000 hover:bg-secondary-200',
        secondary: 'border-contrast-1000 bg-transparent text-contrast-1000 hover:outline-1 hover:outline-contrast-1000',
        ghost: 'border-transparent hover:bg-greyscale-100',
        tertiary: '!h-6 !py-0 gap-3 flex items-center content-center border-0 hover:underline',
        link: 'h-auto border-transparent bg-transparent px-0 py-0 text-primary-brand hover:underline focus-visible:underline',
        // TODO: Change hover behaviour in design review - current hover colour is not accessible
        warning: 'border-warning-400 bg-warning-400 text-contrast-1000 hover:bg-warning-500',
      },
      size: {
        default: 'h-14 px-6 py-2',
        inline: '',
        sm: 'h-9 rounded-md px-3',
        md: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
